/**
 * @file routes/_app/settings/roles.index.tsx
 * @description 角色權限管理列表頁（Task 11）。
 *
 * 功能：
 * - 表格展示所有角色（名稱/代碼/工作台類型/用戶數/狀態/內置 Tag/操作）
 * - 「新建角色」Modal（名稱、代碼可選、工作台類型 Select、複製自模板 Select）
 * - 刪除角色（modal.confirm 二次確認）
 * - 點擊名稱或「編輯」跳至詳情頁
 *
 * 視覺：ListPageShell + antd Table + StatusPill
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Alert, Button, Form, Input, Modal, Select, Space, Table, Tag, Tooltip } from 'antd';
import { modal } from '@/lib/antd-app';
import type { ColumnsType } from 'antd/es/table';
import { Edit3, Plus, Settings, Shield, Trash2, Users } from 'lucide-react';
import { useState } from 'react';
import { WORKBENCH_TYPES, WORKBENCH_TYPE_LABELS, USER_ROLES, USER_ROLE_LABELS } from '@zhcb/shared';
import { useRoleList, useCreateRole, useDeleteRole, type RoleListItem } from '@/services/roles';
import { ListPageShell, PageMetaItem, StatusPill } from '@/components/shell';
import { color, font, text, weight } from '@/styles/tokens';

/** TanStack Router 路由定義 */
export const Route = createFileRoute('/_app/settings/roles/')({
  component: RolesIndexPage,
});

/** 工作台類型 → StatusPill tone 映射 */
const workbenchToneMap: Record<string, 'brand' | 'info' | 'warning' | 'success' | 'neutral'> = {
  admin: 'brand',
  manager: 'warning',
  decider: 'info',
  auditor: 'success',
  accountant: 'neutral',
};

/** 內置角色 Select 選項（複製自模板；含所有 USER_ROLES） */
const BUILTIN_ROLE_OPTIONS = USER_ROLES.map((r) => ({
  value: r,
  label: USER_ROLE_LABELS[r],
}));

/**
 * 角色權限管理列表頁主組件。
 *
 * 從後端取得角色列表後以表格展示，支援新建與刪除操作。
 * 詳情編輯由跳轉至 `/settings/roles/:code` 詳情頁完成。
 */
function RolesIndexPage() {
  const navigate = useNavigate();
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [form] = Form.useForm();

  const { data, isLoading } = useRoleList();
  const createRole = useCreateRole();
  const deleteRole = useDeleteRole();

  const items = data?.items ?? [];
  const total = items.length;
  const activeCount = items.filter((r) => r.status === 'active').length;
  const customCount = items.filter((r) => !r.isBuiltin).length;

  /** 開啟新建 Modal */
  const handleOpenCreate = () => {
    form.resetFields();
    setCreateModalOpen(true);
  };

  /** 提交新建 Modal */
  const handleCreate = () => {
    void form
      .validateFields()
      .then(
        (values: {
          name: string;
          code?: string;
          description?: string;
          workbenchType: string;
          cloneFrom?: string;
        }) => {
          createRole.mutate(
            {
              name: values.name,
              code: values.code || undefined,
              description: values.description || undefined,
              workbenchType: values.workbenchType as (typeof WORKBENCH_TYPES)[number],
              cloneFrom: values.cloneFrom || undefined,
            },
            {
              onSuccess: (res) => {
                setCreateModalOpen(false);
                // 跳轉至新建角色的詳情頁
                void navigate({
                  to: '/settings/roles/$code',
                  params: { code: res.item.code },
                });
              },
            },
          );
        },
      );
  };

  /** 刪除角色二次確認 */
  const handleDelete = (record: RoleListItem) => {
    void modal.confirm({
      title: `刪除角色「${record.name}」？`,
      content:
        record.userCount > 0
          ? `目前有 ${record.userCount} 位用戶使用此角色，刪除後這些用戶將無法登入。`
          : '此操作不可撤回。',
      okText: '確認刪除',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: () => {
        deleteRole.mutate(record.code);
      },
    });
  };

  /** 表格欄位定義 */
  const columns: ColumnsType<RoleListItem> = [
    {
      title: '角色名稱',
      dataIndex: 'name',
      width: 200,
      ellipsis: { showTitle: true },
      render: (name: string, record) => (
        <Space size={6}>
          <Button
            type="link"
            style={{
              fontWeight: weight.semibold,
              color: color.text.primary,
              padding: 0,
              height: 'auto',
            }}
            onClick={() =>
              void navigate({
                to: '/settings/roles/$code',
                params: { code: record.code },
              })
            }
          >
            {name}
          </Button>
          {record.isBuiltin && (
            <Tag color="blue" style={{ fontSize: text.xs, lineHeight: '18px', padding: '0 5px' }}>
              內置
            </Tag>
          )}
        </Space>
      ),
    },
    {
      title: '角色代碼',
      dataIndex: 'code',
      width: 160,
      ellipsis: { showTitle: true },
      render: (v: string) => (
        <span style={{ fontFamily: font.mono, fontSize: text.sm, color: color.text.secondary }}>
          {v}
        </span>
      ),
    },
    {
      title: '工作台類型',
      dataIndex: 'workbenchType',
      width: 140,
      render: (v: string) => (
        <StatusPill tone={workbenchToneMap[v] ?? 'neutral'} size="sm">
          {WORKBENCH_TYPE_LABELS[v as keyof typeof WORKBENCH_TYPE_LABELS] ?? v}
        </StatusPill>
      ),
    },
    {
      title: '用戶數',
      dataIndex: 'userCount',
      width: 80,
      align: 'right',
      render: (v: number) => (
        <span style={{ fontFamily: font.mono, fontWeight: weight.medium }}>{v}</span>
      ),
    },
    {
      title: '狀態',
      dataIndex: 'status',
      width: 90,
      render: (v: string) => (
        <StatusPill tone={v === 'active' ? 'success' : 'muted'} size="sm" dot>
          {v === 'active' ? '啟用' : '停用'}
        </StatusPill>
      ),
    },
    {
      title: '操作',
      key: '_actions',
      width: 100,
      fixed: 'right' as const,
      render: (_, record) => (
        <Space size={4}>
          <Tooltip title="編輯角色">
            <Button
              type="text"
              size="small"
              icon={<Edit3 size={14} />}
              onClick={() =>
                void navigate({
                  to: '/settings/roles/$code',
                  params: { code: record.code },
                })
              }
            />
          </Tooltip>
          <Tooltip title={record.isBuiltin ? '內置角色不可刪除' : '刪除角色'}>
            <Button
              type="text"
              size="small"
              danger
              icon={<Trash2 size={14} />}
              disabled={record.isBuiltin}
              onClick={() => handleDelete(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <>
      {/* ── 頁面殼 ── */}
      <ListPageShell
        title="角色權限管理"
        description="管理系統中所有角色的能力集與菜單可見性，動態 RBAC 配置。"
        meta={
          <>
            <PageMetaItem icon={<Shield size={13} />} label="角色總數" value={`${total} 個`} />
            <PageMetaItem icon={<Users size={13} />} label="啟用中" value={`${activeCount} 個`} />
            <PageMetaItem
              icon={<Settings size={13} />}
              label="自定義"
              value={`${customCount} 個`}
            />
          </>
        }
        actions={
          <Button type="primary" icon={<Plus size={14} />} onClick={handleOpenCreate}>
            新建角色
          </Button>
        }
      >
        <Table<RoleListItem>
          rowKey="code"
          columns={columns}
          dataSource={items}
          loading={isLoading}
          pagination={false}
          scroll={{ x: 800 }}
          size="small"
        />
      </ListPageShell>

      {/* ── 新建角色 Modal ── */}
      <Modal
        title="新建角色"
        open={createModalOpen}
        onOk={handleCreate}
        onCancel={() => setCreateModalOpen(false)}
        okText="建立"
        cancelText="取消"
        confirmLoading={createRole.isPending}
        destroyOnClose
      >
        <Alert
          type="info"
          showIcon
          message="代碼與名稱說明"
          description="代碼（英數字底線連字號）如不填寫，系統自動從名稱生成。工作台類型決定該角色使用哪種佈局殼與菜單基準樹。"
          style={{ marginBottom: 16 }}
        />
        <Form form={form} layout="vertical">
          <Form.Item
            label="角色名稱"
            name="name"
            rules={[{ required: true, message: '請輸入角色名稱' }]}
          >
            <Input placeholder="例如：驗證助理" maxLength={100} />
          </Form.Item>
          <Form.Item
            label="角色代碼（可選，留空自動生成）"
            name="code"
            rules={[
              {
                pattern: /^[a-z0-9_-]+$/,
                message: '代碼只允許小寫英數字、底線、連字號',
              },
            ]}
          >
            <Input placeholder="例如：verification_assistant" maxLength={50} />
          </Form.Item>
          <Form.Item
            label="工作台類型"
            name="workbenchType"
            rules={[{ required: true, message: '請選擇工作台類型' }]}
          >
            <Select
              placeholder="請選擇"
              options={WORKBENCH_TYPES.map((t) => ({
                value: t,
                label: WORKBENCH_TYPE_LABELS[t],
              }))}
            />
          </Form.Item>
          <Form.Item label="複製自模板（可選）" name="cloneFrom">
            <Select placeholder="不選則從空能力集開始" allowClear options={BUILTIN_ROLE_OPTIONS} />
          </Form.Item>
          <Form.Item label="描述（可選）" name="description">
            <Input.TextArea placeholder="角色用途說明" rows={3} maxLength={500} showCount />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
